<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { timeZoneOptions } from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';
import { WEEKDAYS, weekdayLabel } from '../../../constants/csmlFieldOptions';

const props = defineProps({
  node: { type: Object, required: true },
});

const emit = defineEmits(['patch']);

const { t } = useI18n();

const timezones = timeZoneOptions();

const days = computed(() => props.node.workingDays || []);

const toggleDay = value => {
  emit('patch', {
    workingDays: days.value.includes(value)
      ? days.value.filter(day => day !== value)
      : [...days.value, value],
  });
};

const weekdays = WEEKDAYS.map(day => ({
  value: day.value,
  label: weekdayLabel(day.dayIndex),
}));
</script>

<template>
  <div class="flex flex-col gap-3">
    <label class="flex items-center gap-2 text-sm text-n-slate-12">
      <Switch
        :model-value="!!node.enableWorkingHours"
        @update:model-value="emit('patch', { enableWorkingHours: $event })"
      />
      {{ t('AGENT_BOTS.VISUAL_BUILDER.ENABLED') }}
    </label>
    <div class="flex flex-col gap-1">
      <span class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.TIMEZONE') }}
      </span>
      <ComboBox
        :model-value="node.timezone || 'UTC'"
        :options="timezones"
        @update:model-value="emit('patch', { timezone: $event })"
      />
    </div>
    <div class="flex flex-col gap-1">
      <span class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.WORKING_DAYS') }}
      </span>
      <div class="flex flex-wrap gap-1">
        <button
          v-for="day in weekdays"
          :key="day.value"
          type="button"
          class="px-2 py-1 text-xs font-medium border rounded-md"
          :class="
            days.includes(day.value)
              ? 'border-n-brand bg-n-brand text-white'
              : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:border-n-slate-7'
          "
          @click="toggleDay(day.value)"
        >
          {{ day.label }}
        </button>
      </div>
    </div>
    <Input
      :model-value="node.workingStart"
      type="time"
      size="sm"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.START_TIME')"
      @update:model-value="emit('patch', { workingStart: $event })"
    />
    <Input
      :model-value="node.workingEnd"
      type="time"
      size="sm"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.END_TIME')"
      @update:model-value="emit('patch', { workingEnd: $event })"
    />
    <TextArea
      :model-value="node.outsideMessage"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.OUTSIDE_MESSAGE')"
      :message="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.OUTSIDE_MESSAGE_HINT')"
      @update:model-value="emit('patch', { outsideMessage: $event })"
    />
  </div>
</template>
